import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  IFormErrorNotification,
  INotification,
  NotificationTypes,
} from '../models/notification.model';
import {
  NotificationActions,
  NotificationSelectors,
  NotificationStore,
} from '../store';

@Injectable()
export class NotificationService {
  constructor(protected store: Store<NotificationStore.NotificationState>) {}

  /**
   *  getNotification will return general snackbar related message
   */
  getNotification(): Observable<INotification> {
    return this.store.pipe(select(NotificationSelectors.getNotification));
  }

  /**
   *  getDeploymentNotification will return general snackbar related deployment message
   */
   getDeploymentNotification(): Observable<INotification> {
    return this.store.pipe(select(NotificationSelectors.getDeploymentNotification));
  }

  /**
   *  getPageNotification will return page related message for display on top of the page
   */
  getPageNotification(): Observable<INotification> {
    return this.store.pipe(select(NotificationSelectors.getPageNotification));
  }

  /**
   *  getFormErrorNotification will return Form related error message for display on top of the page
   */
  getFormErrorNotification(): Observable<IFormErrorNotification> {
    return this.store.pipe(
      select(NotificationSelectors.getFormErrorNotification)
    );
  }

  /**
   *   clear snackbar related message
   */
  clearNotification(): void {
    this.store.dispatch(NotificationActions.clearNotification());
  }


  /**
 *   clear snackbar related message
 */
  clearDeploymentNotification(): void {
    this.store.dispatch(NotificationActions.clearDeploymentNotification());
  }

  /**
   *  clear notifciation for display on top of the page
   */
  clearPageNotification(): void {
    this.store.dispatch(NotificationActions.clearPageNotification());
  }

  /**
   *  clear Form Error notifciation for display on top of the page
   */
  clearFormErrorNotification(): void {
    this.store.dispatch(NotificationActions.clearFormErrorNotification());
  }

  /**
   * Sets Notification for Snackbar
   * @param notification of type INotifcation
   */
  setNotification(notification: INotification): void {
    this.store.dispatch(
      NotificationActions.setNotification({ data: notification })
    );
  }

  /**
   * Sets Notification for Page header message
   * @param notification of type INotifcation
   */
  setPagesetNotification(notification: INotification): void {
    this.store.dispatch(
      NotificationActions.setPageNotification({ data: notification })
    );
  }

  /**
   * Sets success message in the store for Snackbar
   * @param text of type text
   */
  setSuccessMessage(text: string, title?: string): void {
    const notification: INotification = {
      title,
      text,
      type: NotificationTypes.Success,
      timeout: 10000,
    };

    this.store.dispatch(
      NotificationActions.setNotification({ data: notification })
    );
  }


  /**
   * Sets deploymnent warning message in the store for Snackbar
   * @param text of type text
   */
   setDeploymentWarningMessage(text: string, title?: string): void {
    const notification: INotification = {
      title,
      text,
      type: NotificationTypes.Warning,
      timeout: 0,
      disableClose: true,
      horizontalPosition: 'end', // Right
      verticalPosition: 'bottom', // Bottom
      showTimer: true,
    };

    this.store.dispatch(
      NotificationActions.setDeploymentNotification({ data: notification })
    );
  }

  /**
   * Sets success message in the store for Snackbar
   * @param text of type text
   */
  setInfoMessage(text: string, title?: string): void {
    const notification: INotification = {
      title,
      text,
      type: NotificationTypes.Info,
      timeout: 10000,
    };

    this.store.dispatch(
      NotificationActions.setNotification({ data: notification })
    );
  }

  /**
   * Sets warning message for Snackbar
   * @param notification of type INotifcation
   */
  setWarningMessage(text: string, title?: string): void {
    const notification: INotification = {
      title,
      text,
      type: NotificationTypes.Warning,
      timeout: 10000,
    };
    this.store.dispatch(
      NotificationActions.setNotification({ data: notification })
    );
  }

  /**
   * Sets error message for Snackbar
   * @param notification of type INotifcation
   */
  setErrorMessage(text: string, title?: string): void {
    const notification: INotification = {
      title,
      text,
      type: NotificationTypes.Error,
      timeout: 13000,
    };

    this.store.dispatch(
      NotificationActions.setNotification({ data: notification })
    );
  }

  /**
   * Sets page error message - show message on header of the page
   * @param message
   * @param [title]
   */
  setPageErrorMessage(message: string, title?: string): void {
    const notification: INotification = {
      text: message,
      title,
      type: NotificationTypes.Error,
      timeout: 1000,
    };

    this.setPagesetNotification(notification);
  }

  /**
   * Sets page warning message - show message on header of the page
   * @param message
   * @param [title]
   */
  setPageWarningMessage(message: string, title?: string): void {
    const notification: INotification = {
      text: message,
      title,
      type: NotificationTypes.Warning,
      timeout: 1000,
    };

    this.setPagesetNotification(notification);
  }

  /**
   * Sets page info message - show message on header of the page
   * @param message
   * @param [title]
   */
  setPageInfoMessage(message: string, title?: string): void {
    const notification: INotification = {
      text: message,
      title,
      type: NotificationTypes.Info,
      timeout: 1000,
    };

    this.setPagesetNotification(notification);
  }

  /**
   * Sets page success message - show message on header of the page
   * @param message
   * @param [title]
   */
  setPageSuccessMessage(message: string, title?: string): void {
    const notification: INotification = {
      text: message,
      title,
      type: NotificationTypes.Success,
      timeout: 1000,
    };

    this.setPagesetNotification(notification);
  }

  /**
   * Sets page error message - show message on header of the page
   * @param message
   * @param [title]
   */
  setFormErrorMessage(
    title: string,
    messages: { controlName: string; message: string }[]
  ): void {
    const notification: IFormErrorNotification = {
      formErrors: messages,
      title,
      type: NotificationTypes.Error,
    };
    this.store.dispatch(
      NotificationActions.setFormErrorNotification({ data: notification })
    );
  }
}
